// The quote list (round 43, spec 12).
//
// Zanetto Tools' list, in Dayboard's idiom: the default tab is the open
// pipeline (Draft + Sent), each status has its own tab, and All and Archived
// sit on the right. The total under the list is the SERVER's sum of everything
// the filter matched, not a sum of the rows this page happens to hold (ZT
// summed what it had, which is wrong the moment a list is capped).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api';
import { formatDate } from '../lib/dates';
import { Banner } from '../ui';
import { EmptyState, PageHeader } from '../components/ui/Page';
import { StatusBadge } from '../components/ui/StatusBadge';
import { CardList, RowCard } from '../components/ui/RowCard';
import { LinkedRow } from '../components/ui/LinkedRow';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';
import { useLatestRequest } from '../lib/useLatestRequest';
import { QUOTE_STATUS_LABEL, formatCents, quotesApi, type QuoteSummary } from '../quotesApi';
import { CsvImport } from '../components/ui/CsvImport';
import { RevisionPill } from './QuoteDetailPage';
import { useOptionalSession } from '../session';

/**
 * The import template (round 43, release 4): one quote per row, the amount
 * excluding GST. Dates matter: a won or lost quote takes `date_decided`, so
 * history lands in the month it happened rather than the month it was typed.
 */
const IMPORT_TEMPLATE =
  'client,title,amount,status,quote_date,date_decided,valid_until,site_address,notes\n' +
  'Redgum Estates,Driveway and crossover,4850,won,2026-03-02,2026-03-20,,14 Fenwick St Tarneit,Imported from the old system\n';

type SortKey = 'number' | 'title' | 'client' | 'status' | 'due' | 'total';

/** The open pipeline: what is still waiting on somebody. The default tab. */
const OPEN = 'draft,sent';

const TABS: { value: string; label: string }[] = [
  { value: OPEN, label: 'Draft + Sent' },
  ...Object.entries(QUOTE_STATUS_LABEL).map(([value, label]) => ({ value, label })),
  { value: 'all', label: 'All' },
  { value: 'archived', label: 'Archived' },
];

/** "Q-12" sorts by its number, so Q-9 comes before Q-10 (round 34's rule). */
function numberSortValue(number: string): number | string {
  const digits = /(\d+)\D*$/.exec(number);
  return digits ? Number(digits[1]) : number;
}

export function QuotesPage() {
  const isAdmin = useOptionalSession()?.user?.role === 'admin';
  const [quotes, setQuotes] = useState<QuoteSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [totalCents, setTotalCents] = useState(0);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { sortKey, sortDir, onSort } = useSort<SortKey>('number', 'desc');

  // The tab lives in the URL, like the jobs list: worth bookmarking.
  const [params, setParams] = useSearchParams();
  const tab = params.get('status') ?? OPEN;
  const setTab = (next: string) => {
    const updated = new URLSearchParams(params);
    if (next === OPEN) updated.delete('status');
    else updated.set('status', next);
    setParams(updated, { replace: true });
  };

  const begin = useLatestRequest();
  const load = useCallback(async () => {
    const isCurrent = begin();
    try {
      const result = await quotesApi.list({
        status: tab === 'archived' || tab === 'all' ? undefined : tab,
        archived: tab === 'archived',
        search: search || undefined,
      });
      if (!isCurrent()) return;
      setQuotes(result.quotes);
      setTotal(result.total);
      setTotalCents(result.totalCents);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : 'Could not load the quotes.');
    } finally {
      if (isCurrent()) setLoaded(true);
    }
  }, [begin, tab, search]);

  useEffect(() => {
    void load();
  }, [load]);

  /** The next page of the same tab and search, added under what is shown. */
  const [loadingMore, setLoadingMore] = useState(false);
  async function loadMore() {
    setLoadingMore(true);
    const isCurrent = begin();
    try {
      const result = await quotesApi.list({
        status: tab === 'archived' || tab === 'all' ? undefined : tab,
        archived: tab === 'archived',
        search: search || undefined,
        offset: quotes.length,
      });
      if (!isCurrent()) return;
      setQuotes((current) => [...current, ...result.quotes]);
      setTotal(result.total);
      setTotalCents(result.totalCents);
    } catch (err) {
      if (isCurrent()) setError(err instanceof ApiError ? err.message : 'Could not load more quotes.');
    } finally {
      setLoadingMore(false);
    }
  }

  const visible = useMemo(
    () =>
      sortRows(
        quotes,
        (quote) => {
          switch (sortKey) {
            case 'number':
              return numberSortValue(quote.number);
            case 'title':
              return quote.title;
            case 'client':
              return quote.clientName;
            case 'status':
              return QUOTE_STATUS_LABEL[quote.status];
            case 'due':
              return quote.submissionDueDate;
            case 'total':
              return quote.totalCents;
          }
        },
        sortDir
      ),
    [quotes, sortKey, sortDir]
  );

  return (
    <>
      <PageHeader
        title="Quotes"
        actions={
          <>
            {isAdmin && (
              <>
                <Link to="/app/quotes/reports" className="btn-secondary">
                  Reports
                </Link>
                <CsvImport
                  entityLabel="quote"
                  templateName="dayboard-quotes-template.csv"
                  templateCsv={IMPORT_TEMPLATE}
                  requiredHeaders={['client', 'title', 'amount']}
                  previewColumns={[
                    { key: 'client', label: 'Client' },
                    { key: 'title', label: 'Title' },
                    { key: 'amount', label: 'Amount (excl GST)' },
                    { key: 'status', label: 'Status' },
                    { key: 'date_decided', label: 'Decided' },
                  ]}
                  onImport={async (rows) => {
                    try {
                      const result = await quotesApi.importRows(rows);
                      void load();
                      return result.imported;
                    } catch (err) {
                      // The server names every bad row; say them all, not just
                      // that the file was refused.
                      const rowErrors = (err as ApiError).details as { errors?: string[] } | undefined;
                      throw new Error(
                        [err instanceof ApiError ? err.message : 'Could not import that file.', ...(rowErrors?.errors ?? [])].join(' ')
                      );
                    }
                  }}
                />
              </>
            )}
            <Link to="/app/quotes/new" className="btn-primary">
              New quote
            </Link>
          </>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}

      <div
        className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200"
        role="tablist"
        aria-label="Filter quotes by status"
      >
        {TABS.map(({ value, label }) => {
          const active = tab === value;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(value)}
              className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                active
                  ? 'border-brand-700 font-medium text-brand-800'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-80">
          <label className="form-label" htmlFor="quote-search">
            Search
          </label>
          <input
            id="quote-search"
            type="search"
            className="form-input"
            placeholder="Search by number, title or client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {quotes.length < total && (
        <p className="muted mb-3">
          Showing the newest {quotes.length} of {total} quotes. The total below covers all{' '}
          {total}; the button under the list brings in the next ones.
        </p>
      )}

      {loaded && quotes.length === 0 ? (
        <EmptyState
          title={tab === OPEN && !search ? 'No open quotes' : 'No quotes here'}
          body="Price a job for a client and it will appear here, from draft to won or lost."
          action={
            <Link to="/app/quotes/new" className="btn-primary">
              New quote
            </Link>
          }
        />
      ) : (
        <>
          <CardList>
            {visible.map((quote) => (
              <RowCard
                key={quote.id}
                to={`/app/quotes/${quote.id}`}
                title={
                  <>
                    <span className="tabular-nums">{quote.number}</span>
                    {quote.currentRevision > 1 && (
                      <>
                        {' '}
                        <RevisionPill revision={quote.currentRevision} />
                      </>
                    )}{' '}
                    · {quote.title}
                  </>
                }
                badges={<StatusBadge status={quote.status} kind="quote" />}
              >
                <div>{quote.clientName}</div>
                <div className="font-medium tabular-nums text-gray-900">
                  {formatCents(quote.totalCents)}
                </div>
                {quote.submissionDueDate && quote.status === 'draft' && (
                  <div>Due {formatDate(quote.submissionDueDate)}</div>
                )}
                {quote.convertedJobNumber && <div>Became {quote.convertedJobNumber}</div>}
              </RowCard>
            ))}
          </CardList>
          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Number" sortKey="number" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Title" sortKey="title" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Client" sortKey="client" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Submission due" sortKey="due" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Total" sortKey="total" current={sortKey} dir={sortDir} onSort={onSort} className="text-right" />
                </tr>
              </thead>
              <tbody>
                {visible.map((quote) => (
                  <LinkedRow key={quote.id} to={`/app/quotes/${quote.id}`}>
                    <td className="font-medium tabular-nums whitespace-nowrap">
                      <Link to={`/app/quotes/${quote.id}`} className="table-link">
                        {quote.number}
                      </Link>
                      {/* ZT's revision pill (round 45). */}
                      {quote.currentRevision > 1 && (
                        <span className="ml-2 align-middle">
                          <RevisionPill revision={quote.currentRevision} />
                        </span>
                      )}
                    </td>
                    <td>{quote.title}</td>
                    <td className="table-muted">{quote.clientName}</td>
                    <td className="whitespace-nowrap">
                      <StatusBadge status={quote.status} kind="quote" />
                      {quote.convertedJobNumber && (
                        <span className="table-muted ml-2 text-xs">
                          became {quote.convertedJobNumber}
                        </span>
                      )}
                    </td>
                    <td className="table-muted whitespace-nowrap">
                      {quote.submissionDueDate ? formatDate(quote.submissionDueDate) : '—'}
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums">
                      {formatCents(quote.totalCents)}
                    </td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>
          {quotes.length < total && (
            <div className="mt-3">
              <button type="button" className="btn-secondary" disabled={loadingMore} onClick={() => void loadMore()}>
                {loadingMore ? 'Loading…' : `Show the next ${Math.min(200, total - quotes.length)}`}
              </button>
            </div>
          )}
          {quotes.length > 0 && (
            <p className="mt-3 text-right text-sm text-gray-700">
              {total} {total === 1 ? 'quote' : 'quotes'} · <span className="font-semibold tabular-nums">{formatCents(totalCents)}</span>
              <span className="muted"> (current revisions, GST included where it applies)</span>
            </p>
          )}
        </>
      )}
    </>
  );
}
